import { useMemo, type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import type { Granularity, SeriesMetricResult } from '@/types/analyticsV2';
import type { MetricDef } from '../metric-defs';

interface MetricChartProps {
  def: MetricDef;
  series: SeriesMetricResult | undefined;
  isLoading: boolean;
  /**
   * Optional control rendered in the container's header row (e.g. the metric
   * selector dropdown). Lets the route own the control while keeping its
   * placement inside the chart container.
   */
  selector?: ReactNode;
  /** Right side of the header row, e.g. the granularity selector. */
  controls?: ReactNode;
}

const tickDateFormat = (granularity: Granularity): string => {
  switch (granularity) {
    case 'day': return 'MMM d';
    case 'week': return 'MMM d';
    case 'month': return 'MMM';
  }
};

const formatYValue = (n: number, format: 'number' | 'percent') =>
  format === 'percent' ? `${n}%` : n.toLocaleString();

export function MetricChart({ def, series, isLoading, selector, controls }: MetricChartProps) {
  const chart = def.chart;

  const data = useMemo(() => {
    if (!series || !chart) return [];
    const fmt = tickDateFormat(series.granularity);
    return series.buckets.map((b) => {
      const point: Record<string, number | string | null> = {
        date: b.date,
        label: format(parseISO(b.date), fmt),
      };
      for (const s of chart.series) {
        point[s.field] = b[s.field];
      }
      return point;
    });
  }, [series, chart]);

  if (!chart) {
    return null;
  }

  return (
    <div className='rounded-3xl border border-grey-200 bg-white p-5 flex flex-col gap-8 h-[420px]'>
      <div className='flex items-start justify-between gap-4 shrink-0'>
        <div className='flex flex-col gap-1'>
          <div className='flex items-center'>
            {selector}
          </div>
        </div>
        <div className='shrink-0'>{controls}</div>
      </div>

        <div className='flex-1 min-h-0 w-full'>
          {isLoading ? (
            <Skeleton className='h-full w-full rounded-md' />
          ) : data.length === 0 ? (
            <div className='h-full w-full flex items-center justify-center text-sm text-grey-500'>
              No data for the selected period.
            </div>
          ) : (
            <ResponsiveContainer width='100%' height='100%'>
              <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <defs>
                  {chart.series.map((s) => (
                    <linearGradient key={s.field} id={`grad-${def.key}-${s.field}`} x1='0' y1='0' x2='0' y2='1'>
                      <stop offset='0%' stopColor={s.color} stopOpacity={0.25} />
                      <stop offset='100%' stopColor={s.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray='3 3' stroke='var(--color-gray-300)' vertical={false} />
                <XAxis
                  dataKey='label'
                  tick={{ fontSize: 12, fill: 'var(--color-gray-900)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: 'var(--color-gray-900)' }}
                  axisLine={false}
                  tickLine={false}
                  domain={chart.yDomain}
                  tickFormatter={(v: number) => formatYValue(v, chart.yFormat)}
                  width={48}
                />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid var(--color-gray-300)', fontSize: 12 }}
                  formatter={(value: number) => formatYValue(value, chart.yFormat)}
                  labelFormatter={(label: string) => label}
                />
                {chart.series.map((s) => (
                  <Area
                    key={s.field}
                    type='monotone'
                    dataKey={s.field}
                    name={s.label}
                    stroke={s.color}
                    fill={`url(#grad-${def.key}-${s.field})`}
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

      {!isLoading && data.length > 0 ? (
        <div className='flex items-center gap-4 flex-wrap shrink-0'>
          {chart.series.map((s) => (
            <div key={s.field} className='flex items-center gap-1.5 text-xs text-grey-700'>
              <span className='inline-block h-2.5 w-2.5 rounded-full' style={{ backgroundColor: s.color }} />
              {s.label}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
